import { map } from 'rxjs';

import { Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ApmTranslatePipe, ResourcesService } from '@mitsubishi/core';
import { UNIT_SYSTEM_OPTIONS } from '@mitsubishi/shared/data-access';
import {
  ApmFieldErrorsComponent,
  InputDirective,
  SectionTitleComponent,
  SelectModule,
  ValidationErrorMessage,
} from '@mitsubishi/shared/ui';

import { COND_OUTLET_TYPES, EVAP_INLET_TYPES } from '../../chiller-free-st.constants';
import { FreeStUtils } from '../../chiller-free-st.utils';
import {
  ConfigurationRequest,
  FreestFieldMappingDto,
  SheetRequest,
  UomSystem,
} from '../../models/chiller-free-st.model';
import { ChillerFreeStDataSheetStore } from '../../stores/chiller-free-st-data-sheet.store';
import { ChillerFreeStStore } from '../../stores/chiller-free-st.store';
import { ConvertibleInputComponent } from '../convertible-input/convertible-input.component';

const DEFAULT_LOAD_PCTS = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10];

@Component({
  selector: 'apm-chiller-free-st-data-sheet-working-conditions',
  imports: [
    ReactiveFormsModule,
    ApmTranslatePipe,
    InputDirective,
    SelectModule,
    SectionTitleComponent,
    ConvertibleInputComponent,
    ApmFieldErrorsComponent,
  ],
  templateUrl: './chiller-free-st-data-sheet-working-conditions.component.html',
  styleUrl: './chiller-free-st-data-sheet-working-conditions.component.scss',
})
export class ChillerFreeStDataSheetWorkingConditionsComponent {
  readonly mainStore = inject(ChillerFreeStStore);
  readonly store = inject(ChillerFreeStDataSheetStore);
  #resourcesService = inject(ResourcesService);

  readonly unitSystemOptions = UNIT_SYSTEM_OPTIONS;
  readonly evapInletTypes = [...EVAP_INLET_TYPES];
  readonly condOutletTypes = [...COND_OUTLET_TYPES];

  readonly loadPctsErrorMessages: ValidationErrorMessage = {
    notDescending: this.#resourcesService.getTextResource(465147),
  };

  readonly form = new FormGroup({
    uomSystem: new FormControl<string>('SI'),
    coolingCapacity: new FormControl<number | null>(null, Validators.required),
    evapInletType: new FormControl<string>('EVA_INLET_TEMP'),
    evapInletValue: new FormControl<number | null>(null, Validators.required),
    evapOutletType: new FormControl<string>({ value: 'EVA_INLET_TEMP', disabled: true }),
    evapOutletTemp: new FormControl<number | null>(null, Validators.required),
    condInletType: new FormControl<string>({ value: 'COND_OUTLET_TEMP', disabled: true }),
    condInletTemp: new FormControl<number | null>(null, Validators.required),
    condOutletType: new FormControl<string>('COND_OUTLET_TEMP'),
    condOutletVal: new FormControl<number | null>(null, Validators.required),
    loadPcts: new FormArray(
      DEFAULT_LOAD_PCTS.map((v, i) =>
        i === 0
          ? new FormControl<number>({ value: 100, disabled: true })
          : new FormControl<number>(v, [Validators.required, Validators.min(1), Validators.max(100)])
      ),
      [FreeStUtils.loadPctsValidator]
    ),
  });

  readonly isFormValid = toSignal(this.form.statusChanges.pipe(map(() => this.form.valid)), { initialValue: false });

  readonly selectedEvapInletType = toSignal(this.form.controls.evapInletType.valueChanges, {
    initialValue: 'EVA_INLET_TEMP' as string,
  });
  readonly selectedCondOutletType = toSignal(this.form.controls.condOutletType.valueChanges, {
    initialValue: 'COND_OUTLET_TEMP' as string,
  });

  readonly fieldMappings = computed(() => {
    const fieldMap = this.mainStore.fieldMappingMap();
    const evap = fieldMap['EVAPORATOR'] ?? {};
    const cond = fieldMap['CONDENSER'] ?? {};
    const cooling = fieldMap['COOLING'] ?? {};
    const evapInletType = this.selectedEvapInletType();
    const condOutletType = this.selectedCondOutletType();

    const sym = (m: FreestFieldMappingDto | undefined) => m?.forcedUom ?? m?.symbol ?? '';
    const entry = (m: FreestFieldMappingDto | undefined) => ({ mapping: m, symbol: sym(m) });

    return {
      coolingCapacity: entry(cooling['machineInfo.cap']),
      evapInletValue: entry(evapInletType === 'EVA_INLET_FLOW' ? evap['evapWC.flow'] : evap['evapWC.inletTemp']),
      evapOutletTemp: entry(evap['evapWC.outletTemp']),
      condInletTemp: entry(cond['condWC.inletTemp']),
      condOutletVal: entry(condOutletType === 'COND_OUTLET_FLOW' ? cond['condWC.flow'] : cond['condWC.outletTemp']),
    };
  });

  get loadPcts(): FormArray {
    return this.form.controls.loadPcts;
  }

  constructor() {
    const config = this.store.configuration()!;
    const uomSystem = config.uomSystem ?? 'SI';
    this.form.controls.uomSystem.setValue(uomSystem, { emitEvent: false });
    this.patchFormFrom(config);
    this.updateValidators();
    this.form.updateValueAndValidity();
    this.generatePdfFrom(config);

    this.form.controls.evapOutletTemp.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.form.controls.evapInletValue.updateValueAndValidity();
    });

    this.form.controls.condInletTemp.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.form.controls.condOutletVal.updateValueAndValidity();
    });
  }

  onEvapInletTypeChange(): void {
    this.updateValidators();
    this.form.controls.evapInletValue.updateValueAndValidity();
  }

  onCondOutletTypeChange(): void {
    this.updateValidators();
    this.form.controls.condOutletVal.updateValueAndValidity();
  }

  onSystemUnitChange(systemUnit: UomSystem): void {
    if (!systemUnit || systemUnit === this.mainStore.uomSystem()) return;
    this.mainStore.loadFieldMappings(systemUnit).subscribe(() => {
      this.updateDatasheet();
    });
  }

  updateDatasheet(): void {
    const config = this.store.configuration();
    if (!config || this.form.invalid) return;
    this.generatePdfFrom(config);
  }

  private patchFormFrom(config: ConfigurationRequest): void {
    const ud = config.userDataInput;
    this.form.patchValue(
      {
        coolingCapacity: ud.coolingCapacity,
        evapInletType: ud.evapInletType ?? 'EVA_INLET_TEMP',
        evapInletValue: ud.evapInletType === 'EVA_INLET_FLOW' ? ud.evapFlowPerKW : ud.evapInletTemp,
        evapOutletTemp: ud.evapOutletTemp,
        condInletTemp: ud.condInletTemp,
        condOutletType: ud.condOutlet.condOutletType,
        condOutletVal: ud.condOutlet.condOutletVal,
      },
      { emitEvent: false }
    );
  }

  private generatePdfFrom(config: ConfigurationRequest): void {
    if (this.form.invalid) return;

    const formVal = this.form.getRawValue();
    const ud = config.userDataInput;

    const isFlowMode = formVal.evapInletType === 'EVA_INLET_FLOW';
    const evapOutletTemp = formVal.evapOutletTemp!;
    let evapInletTemp: number;
    let evapFlowPerKW: number;

    if (isFlowMode) {
      evapFlowPerKW = formVal.evapInletValue!;
      evapInletTemp = FreeStUtils.calcEvapInletTemp(evapOutletTemp, evapFlowPerKW);
    } else {
      evapInletTemp = formVal.evapInletValue!;
      evapFlowPerKW = FreeStUtils.calcEvapFlowPerKW(evapInletTemp, evapOutletTemp);
    }

    const updatedUserDataInput = {
      ...ud,
      coolingCapacity: formVal.coolingCapacity!,
      evapInletType: formVal.evapInletType!,
      evapInletTemp,
      evapOutletTemp,
      evapFlowPerKW,
      condInletTemp: formVal.condInletTemp!,
      condOutlet: {
        condOutletType: formVal.condOutletType!,
        condOutletVal: formVal.condOutletVal!,
        definiteTemp: formVal.condOutletType === 'COND_OUTLET_TEMP',
      },
    };

    const loadPctList = formVal.loadPcts.map(v => (v ?? 0) / 100);

    const sheetRequest: SheetRequest = {
      evapId: config.evapId,
      condId: config.condId,
      compModelId: config.compModelId,
      specialMachineId: config.specialMachineId!,
      specialMachineDisplayName: config.specialMachineDisplayName ?? undefined,
      loadPctList,
      userName: config.userName,
      userDataInput: updatedUserDataInput,
    };

    this.store.generatePdf(sheetRequest);
  }

  private updateValidators(): void {
    const frontEndLimit = this.mainStore.frontEndLimit();
    if (!frontEndLimit) return;

    FreeStUtils.applyFrontEndLimitValidators(
      {
        evapInletValue: this.form.controls.evapInletValue,
        evapOutletTemp: this.form.controls.evapOutletTemp,
        condInletTemp: this.form.controls.condInletTemp,
        condOutletVal: this.form.controls.condOutletVal,
      },
      frontEndLimit,
      {
        evapInletType: this.form.controls.evapInletType.value ?? 'EVA_INLET_TEMP',
        condOutletType: this.form.controls.condOutletType.value ?? 'COND_OUTLET_TEMP',
      }
    );
  }
}
